import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchUserProfile, login as loginAPI, register as registerAPI } from "services/api/auth";
import { Master, Partner, Timezone } from "types";
import { LoginPayload, RegistrationPayload } from "types/auth";
import { User, UserProfileResponse } from "types/user";
import { setAuthorization } from "utils/axiosClient";
import { setCookie } from "utils/cookie";
import { getErrorMessage, isFresh } from "utils/helpers";
import { AuthState } from "./reducer";
import apiConfig from "constants/apiConfig";
import { getBackendTokenForFirebase, getChatSupportToken } from "services/api/chat";
import { SocialAccountData } from "types/social-account";
import { fetchSocialAccounts } from "services/api/social-account";
import { fetchAgencyMetadataSuggestions } from "services/api/onboarding";
import { fetchAgencyMaster } from "services/api/master";

export const login = createAsyncThunk<string, LoginPayload, {rejectValue: string}>(
    'auth/login',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await loginAPI(payload); 
            if(!response.token) throw new Error("Login failed!");
            setCookie('agencyAuthToken', response.token,  {
                expires: 180,
                path: '/',
                secure: true,
                sameSite: 'Strict'
            });
            setAuthorization(response.token);
            return response.token
        } catch (error) {
            console.log('error', error);
            return rejectWithValue(getErrorMessage(error, 'Login failed!'));
        }
    }
)

export const fetchUser = createAsyncThunk<UserProfileResponse, void, {rejectValue: string}>(
    'auth/fetch-user',
    async (_, {rejectWithValue}) => {
        try {
            const response = await fetchUserProfile();
            return response;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch user profile!'));
        }
    }
)

export const register = createAsyncThunk<string, RegistrationPayload, {rejectValue: string}>(
    'auth/register',
    async (payload, {rejectWithValue}) => {
        try {
            const response = await registerAPI(payload); 
            if(!response.token) throw new Error("Registration failed!");
            setCookie('agencyAuthToken', response.token,  {
                expires: 180,
                path: '/',
                secure: true,
                sameSite: 'Strict'
            });
            setAuthorization(response.token);
            return response.token
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Registration failed!'));
        }
    }
)

export const fetchFirebaseBackendTokenThunk = createAsyncThunk<{token: string, agencyId: string}, boolean, {rejectValue: string}>(
    'auth/firebase-token',
    async (force: boolean, {rejectWithValue, getState}) => {
        try {
            const state = getState() as {auth: AuthState};
            const selectedAgencyId = state.auth.selectedAgencyId;
            const STALE_TIME = apiConfig.CREATE_FIREBASE_TOKEN.staleTime;
            if(!selectedAgencyId) throw new Error("No agency selected!")
            if(!force && state.auth.firebase_tokens[selectedAgencyId] && isFresh(state.auth.firebase_tokens[selectedAgencyId]?.generatedAt, STALE_TIME)){
                return {token: state.auth.firebase_tokens[selectedAgencyId].token, agencyId: selectedAgencyId};
            }
            console.log('force', force);
            const token: string = await getBackendTokenForFirebase(selectedAgencyId);
            return {token, agencyId: selectedAgencyId};            
        } catch (error) {
            return rejectWithValue('Failed to login to firebase!');
        }
    }
)

export const fetchChatSupportTokenThunk = createAsyncThunk<{token: string, agencyId: string}, boolean, {rejectValue: string}>(
    'auth/chat-support-token',
    async (force: boolean, {rejectWithValue, getState}) => {
        try {
            const state = getState() as {auth: AuthState};
            const selectedAgencyId = state.auth.selectedAgencyId;
            const STALE_TIME = apiConfig.CHAT_SUPPORT_TOKEN.staleTime;
            if (!selectedAgencyId) throw new Error("No agency selected!");
            if (
                !force &&
                state.auth.chat_support_tokens[selectedAgencyId] &&
                isFresh(state.auth.chat_support_tokens[selectedAgencyId]?.generatedAt, STALE_TIME)
            ) {
                return {
                    token: state.auth.chat_support_tokens[selectedAgencyId].token,
                    agencyId: selectedAgencyId,
                };
            }
            const token = await getChatSupportToken(selectedAgencyId);
            return {token, agencyId: selectedAgencyId};
        } catch (error) {
            return rejectWithValue('Failed to fetch chat support token!');
        }
    }
)

export const fetchSocialAccountsThunk = createAsyncThunk<{accounts: SocialAccountData[], fetchedAt: number}, {force?: boolean}, {rejectValue: string}>(
    'auth/fetch-social-accounts',
    async ({force=false}, {rejectWithValue, getState}) => {
        try {
            const staleTime = apiConfig.FETCH_SOCIAL_ACCOUNTS.staleTime;
            const state = getState() as {auth: AuthState};
            const selectedAgencyId = state.auth.selectedAgencyId;
            if(!selectedAgencyId) throw new Error("No agency selected!")
            if(!force && isFresh(state.auth.social_accounts.last_fetched_at, staleTime)){
                return {accounts: state.auth.social_accounts.items, fetchedAt: state.auth.social_accounts.last_fetched_at};
            }
            const response = await fetchSocialAccounts(selectedAgencyId);
            const accounts = response?.items ?? [];
            return {accounts, fetchedAt: Date.now()};
        } catch (error) {
            return rejectWithValue('Failed to fetch social accounts!');
        }
    }
)

export const fetchAgencyMetadataSuggestionsThunk = createAsyncThunk<{key: string, values: string[]}[], void, {rejectValue: string}>(
    'auth/fetch-agency-metadata-suggestions',
    async (_, {rejectWithValue, getState}) => {
        try {
            const state = getState() as {auth: AuthState};
            const selectedAgencyId = state.auth.selectedAgencyId;
            if(!selectedAgencyId) throw new Error("No agency selected!")
            const response = await fetchAgencyMetadataSuggestions(selectedAgencyId);
            const suggestions = response?.items ?? [];
            return suggestions;
        } catch (error) {
            console.log('error', error);
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch agency metadata suggestions!'));
        }
    }
)

export const fetchAgencyMasterThunk = createAsyncThunk<{master: Master, partner: Partner, timezone_list: Timezone[]}, void, {rejectValue: string}>(
    'auth/fetch-agency-master',
    async (_, {rejectWithValue, getState}) => {
        try {
            const state = getState() as {auth: AuthState};
            const selectedAgencyId = state.auth.selectedAgencyId;
            if(!selectedAgencyId) throw new Error("No agency selected!")
            const response = await fetchAgencyMaster(selectedAgencyId);
            return {master: response.master, partner: response.partner, timezone_list: response.master.timezone_list};
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch agency master!'));
        }
    }
)